/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-this-alias */
import L from 'lodash'

import { renderInputValidity } from './validationUtils'
import { confirmValidator } from './validationRules'
import {
  blurEvent,
  returnKeyEvent,
} from '@straal/assets/staticViews/scripts/helpers.ts'

export function Store({ submitButton, fields }) {
  const self = this

  this.token = String
  this.inputsData = Object
  this.submitted = Boolean
  this.submitButton = Element

  // INITIALIZE FIELDS
  this.submitted = false
  this.submitButton = submitButton

  fields.map(input => {
    const { element } = input
    const value = element.value || null

    self.inputsData[input.name] = {
      validity: {
        valid: true,
        errors: null,
      },
      element,
      validator: null,
      dirty: value !== null,
    }

    self.attachFieldHandler(input)
  })
}

Store.prototype.get = function(path, defaultValue) {
  return L.get(this, path, defaultValue)
}
Store.prototype.set = function(path, value) {
  const normalizedValue =
    !L.isNil(value) && value.toString().length ? value : null

  L.set(this, path, normalizedValue)
}
Store.prototype.getInputField = function(inputName) {
  return this.get(`inputsData.${inputName}`, null)
}
Store.prototype.getInputElement = function(inputName) {
  return this.get(`inputsData.${inputName}.element`, null)
}
Store.prototype.getInputValue = function(inputName) {
  const element = this.getInputElement(inputName)
  return element ? element.value : null
}
Store.prototype.setInputValidity = function(inputName, inputValidity) {
  if (inputName) {
    this.set(`inputsData.${inputName}.validity`, inputValidity)
  } else {
    console.error(`Cannot set validity of ${inputName}`)
  }
}
Store.prototype.getInputValidity = function(inputName) {
  return this.get(`inputsData.${inputName}.validity`, null)
}
Store.prototype.setInputValidator = function(inputName, validator) {
  this.set(`inputsData.${inputName}.validator`, validator)
}
Store.prototype.getInputValidator = function(inputName) {
  return this.get(`inputsData.${inputName}.validator`, null)
}
Store.prototype.setInputDirty = function(inputName, dirty) {
  this.set(`inputsData.${inputName}.dirty`, dirty)
}
Store.prototype.getInputDirty = function(inputName) {
  return this.get(`inputsData.${inputName}.dirty`, null)
}
Store.prototype.setFormSubmitted = function(submitted) {
  this.set('submitted', submitted)
}
Store.prototype.setToken = function(token) {
  this.set('token', token)
}
Store.prototype.getToken = function() {
  return this.get('token', null)
}
Store.prototype.displayInputValidity = function(inputName, inputValidity) {
  if (inputValidity) {
    const inputData = this.getInputField(inputName)

    const isFieldDirty = this.getInputDirty(inputName)
    const isFormSubmitted = this.submitted

    if (isFieldDirty || isFormSubmitted) {
      renderInputValidity(inputData.element, inputValidity)
    }
  }
}
Store.prototype.displayFormValidity = function() {
  L.forEach(this.inputsData, (inputData, inputName) => {
    const inputValidity = this.getInputValidity(inputName)
    this.displayInputValidity(inputName, inputValidity)
  })
}
Store.prototype.validateField = async function(inputName) {
  const inputData = this.getInputField(inputName)
  const inputEl = inputData.element
  const inputValue = this.getInputValue(inputName)
  const inputValidator = this.getInputValidator(inputName)

  const isInputDisabled = inputEl.disabled
  const inputValidity =
    isInputDisabled || !inputValidator
      ? { valid: true, errors: null }
      : await inputData.validator(inputValue, this)

  this.setInputValidity(inputName, inputValidity)
  this.displayInputValidity(inputName, inputValidity)

  return inputValidity
}
Store.prototype.validateForm = function() {
  const inputsDataMapper = async (inputData, inputName) => ({
    name: inputName,
    validity: await this.validateField(inputName),
  })
  const validationPromises = L.map(this.inputsData, inputsDataMapper)

  // Return errors list
  return Promise.all(validationPromises).then(results =>
    L.filter(results, result => !result.validity.valid),
  )
}
Store.prototype.submitForm = function(successCallback, errorCallback) {
  this.setFormSubmitted(true)
  this.validateForm().then(errors => {
    if (errors.length < 1) {
      if (typeof successCallback === 'function') {
        successCallback()
      }
    } else {
      const element = this.getInputElement(errors[0].name)
      if (element) element.focus()
      if (typeof errorCallback === 'function') {
        errorCallback()
      }
    }
  })
}

/*
name:
  field identificator
element:
  field DOM element
validator:
  a) an object containing fields:
    - type: type of build in validation rule (list avaiable in validationRules.js)
    - args: object containing data specific for a given rule
  b) custom validator function receiving field value as an argument
delay:
  time delay in ms to debounce field validation
 */
Store.prototype.attachFieldHandler = function({
  name,
  element,
  validator,
  delay,
}) {
  const self = this
  let fieldValidator

  if (L.isFunction(validator)) {
    fieldValidator = validator
  } else if (L.isObject(validator)) {
    switch (validator.type) {
      case 'confirm':
        // Validate confirm field if compareWith target changes
        fieldValidator = L.partial(confirmValidator, validator.args)

        const otherField = self.inputsData[validator.args.compareWith]
        const validation = () => self.validateField(name)

        otherField.element.addEventListener(
          'blur',
          L.partial(blurEvent, validation),
        )

        break
    }
  }

  self.setInputValidator(name, fieldValidator)

  const handler = async function(event) {
    const isFieldDirty = self.getInputDirty(name)
    if (!isFieldDirty) self.setInputDirty(name, true)
    self.validateField(name)
  }
  const validation = delay ? L.debounce(handler, delay) : handler

  element.addEventListener('blur', L.partial(blurEvent, validation))
  const returnKeyEventHandler = event => {
    event.preventDefault()
    self.submitButton.click()
  }
  element.addEventListener(
    'keydown',
    L.partial(returnKeyEvent, returnKeyEventHandler),
  )
}
